import { useState, useMemo } from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import { useGlobal } from '../../context/GlobalContext';
import { formatPriceText, isPriceKey } from '../../lib/currency';
import { isEnglishLocale } from '../../lib/locale';
import styles from './AvailabilityList.module.css';
import iconCotizar from '../../assets/icons/icon_cotizar.svg';
import iconCorreo from '../../assets/icons/icon_correo.svg';
import iconFlechaArriba from '../../assets/icons/icon_flecha_arriba.svg';
import iconFlechaAbajo from '../../assets/icons/icon_flecha_abajo.svg';

const MAIN_FEATURE_GROUPS = [
  { id: 'price', patterns: ['precio', 'price'] },
  { id: 'area', patterns: ['total m2'] },
  { id: 'type', patterns: ['tipo', 'type'] },
];

function normalizeKey(str) {
  return (str || '').trim().toLowerCase();
}

function getFeatureGroupIndex(key) {
  return MAIN_FEATURE_GROUPS.findIndex(({ patterns }) => patterns.some((pattern) => key.includes(pattern)));
}

function classifyFeatures(features, pageTitulo, mostrarPrecio = true, locale = 'es') {
  const main = [];
  const secondary = [];
  let hasTipo = false;
  for (const f of features) {
    const key = normalizeKey(f.atributo);
    const groupIndex = getFeatureGroupIndex(key);
    if (!mostrarPrecio && isPriceKey(key)) {
      continue;
    }
    if (groupIndex !== -1) {
      main.push(f);
      if (key.includes('tipo') || key.includes('type')) hasTipo = true;
    } else {
      secondary.push(f);
    }
  }
  if (!hasTipo && pageTitulo) {
    main.push({ atributo: isEnglishLocale(locale) ? 'Type' : 'Tipo', valor: pageTitulo });
  }
  main.sort((a, b) => {
    const ai = getFeatureGroupIndex(normalizeKey(a.atributo));
    const bi = getFeatureGroupIndex(normalizeKey(b.atributo));
    return ai - bi;
  });
  return { main, secondary };
}

const AvailabilityList = ({
  data,
  items = [],
  pageTitulo = '',
  cotizadorConfig = null,
  onOpenCotizador,
}) => {
  const { companyInfo, locale } = useGlobal();
  const isEn = isEnglishLocale(locale);
  const titulo = data?.titulo;
  const disclaimer = typeof data?.disclaimer === 'string' && data.disclaimer.trim()
    ? data.disclaimer.trim()
    : (isEn
      ? '*Prices and prototype information are subject to change without prior notice.'
      : '*Los precios e informacion del prototipo estan sujetos a cambios sin previo aviso.');
  const tipoSingular = data?.tipoPropiedad || (isEn ? 'property' : 'propiedad');
  const tipoPlural = data?.tipoPropiedadPlural || (isEn ? 'properties' : 'propiedades');
  const urlContacto = typeof data?.urlContacto === 'string' ? data.urlContacto.trim() : '';
  const interestLabel = typeof data?.textoBotonInteresa === 'string'
    ? data.textoBotonInteresa.trim()
    : '';
  const interesaUseWhatsapp = data?.interesaUseWhatsapp === true;
  const interesaWhatsappNumber = data?.interesaWhatsappNumber || '';
  const interesaWhatsappMessage = data?.interesaWhatsappMessage || '';
  const mostrarPrecio = data?.mostrarPrecio !== false;
  const perPageRaw = parseInt(data?.itemsPorPagina || '0', 10);
  const showAll = perPageRaw <= 0;
  const perPage = showAll ? items.length || 1 : perPageRaw;
  const [currentPage, setCurrentPage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDir, setSlideDir] = useState('');
  const [ref, isVisible] = useScrollReveal({ threshold: 0.1 });

  const totalPages = Math.ceil(items.length / perPage);
  const pageItems = useMemo(
    () => items.slice(currentPage * perPage, (currentPage + 1) * perPage),
    [items, currentPage, perPage]
  );

  // El primer item de cada página empieza expandido
  const defaultExpandedId = useMemo(() => {
    const first = pageItems[0];
    return first ? (first.id ?? first.documentId) : null;
  }, [pageItems]);

  const [expandedState, setExpandedState] = useState(() => ({
    page: 0,
    itemId: defaultExpandedId,
  }));

  const expandedId = expandedState.page === currentPage
    ? expandedState.itemId
    : defaultExpandedId;

  const toggleItem = (itemId) => {
    setExpandedState((prev) => ({
      page: currentPage,
      itemId: prev.page === currentPage && prev.itemId === itemId ? null : itemId,
    }));
  };

  const handlePageChange = (newPage, direction) => {
    if (isAnimating || newPage < 0 || newPage >= totalPages) return;
    setSlideDir(direction);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentPage(newPage);
      setSlideDir(direction === 'left' ? 'enter-right' : 'enter-left');
      setTimeout(() => {
        setIsAnimating(false);
        setSlideDir('');
      }, 300);
    }, 250);
  };

  if (!items.length) return null;

  const getItemId = (item) => item?.id ?? item?.documentId;

  const resultLabel = isEn ? 'found' : 'encontrados';
  const subtitulo = `${items.length} ${items.length === 1 ? tipoSingular : tipoPlural} ${resultLabel}`;
  const hasCotizador = Boolean(cotizadorConfig);
  const showInterestButton = Boolean(urlContacto) || interesaUseWhatsapp;
  const quoteLabel = cotizadorConfig?.textoBotonCotizar || (isEn ? 'Quote' : 'Cotizar');

  const formatFeatureValue = (feature) => {
    if (isPriceKey(feature?.atributo)) {
      return formatPriceText(feature?.valor, companyInfo, locale) || '—';
    }

    return feature?.valor || '—';
  };

  const handleCotizadorClick = (event, item) => {
    event.stopPropagation();

    onOpenCotizador?.(item);
  };

  const getFallbackWhatsappNumber = () => {
    const fromSocial = companyInfo?.socialMedia?.find((entry) => entry?.platform === 'whatsapp');
    if (fromSocial?.url) {
      const match = fromSocial.url.match(/(\d[\d\s\-+()]+)/);
      if (match) return match[1];
    }

    const fromContact = companyInfo?.contactInfo?.find((entry) => entry?.type === 'whatsapp');
    if (fromContact?.value) return fromContact.value;

    return null;
  };

  const buildWhatsappHref = (rawNumber, message, itemTitle) => {
    const number = (rawNumber || '').replace(/\D/g, '');
    if (!number) return null;

    const base = `https://wa.me/${number}`;
    const messageParts = [];

    if (message && message.trim()) {
      messageParts.push(message.trim());
    }

    if (itemTitle && itemTitle.trim()) {
      messageParts.push(itemTitle.trim());
    }

    if (messageParts.length > 0) {
      return `${base}?text=${encodeURIComponent(messageParts.join('\n\n'))}`;
    }

    return base;
  };

  // Derive column headers from the first item's main features
  const firstItem = items[0];
  const { main: firstMain } = classifyFeatures(firstItem?.features || [], pageTitulo, mostrarPrecio, locale);
  const columnHeaders = [isEn ? 'Name' : 'Nombre', ...firstMain.map((f) => f.atributo)];
  while (columnHeaders.length < 4) columnHeaders.push('');

  return (
    <div
      ref={ref}
      className={`${styles.availability} ${isVisible ? styles['availability--visible'] : ''}`}
    >
      <div className={styles.availability__inner}>
      {titulo && (
        <h2 className={styles.availability__title}>{titulo}</h2>
      )}
      <p className={styles.availability__subtitle}>{subtitulo}</p>

      {/* Table header */}
      <div className={styles.availability__header}>
        {columnHeaders.map((header, i) => (
          <div key={i} className={styles.availability__headerCell}>
            {header}
            {header && (
              <span className={styles.availability__sortIcon}>
                <img src={iconFlechaArriba} alt={isEn ? 'Sort ascending' : 'Orden ascendente'} title={isEn ? 'Sort ascending' : 'Orden ascendente'} className={styles.availability__sortArrow} aria-hidden="true" />
                <img src={iconFlechaAbajo} alt={isEn ? 'Sort descending' : 'Orden descendente'} title={isEn ? 'Sort descending' : 'Orden descendente'} className={styles.availability__sortArrow} aria-hidden="true" />
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Items list */}
      <div
        className={[
          styles.availability__list,
          slideDir === 'left' ? styles['availability__list--exitLeft'] : '',
          slideDir === 'right' ? styles['availability__list--exitRight'] : '',
          slideDir === 'enter-left' ? styles['availability__list--enterLeft'] : '',
          slideDir === 'enter-right' ? styles['availability__list--enterRight'] : '',
        ].filter(Boolean).join(' ')}
      >
        {pageItems.map((item) => {
          const itemId = getItemId(item);
          const isExpanded = expandedId === itemId;
          const { main, secondary } = classifyFeatures(item.features || [], pageTitulo, mostrarPrecio, locale);
          const interesaWhatsappHref = buildWhatsappHref(
            interesaWhatsappNumber || getFallbackWhatsappNumber(),
            interesaWhatsappMessage,
            item.titulo
          );
          const interesaHref = interesaUseWhatsapp && interesaWhatsappHref
            ? interesaWhatsappHref
            : (urlContacto
              ? `${urlContacto}${urlContacto.includes('?') ? '&' : '?'}unidad=${encodeURIComponent(item.titulo)}`
              : undefined);
          const interesaIsWhatsapp = interesaUseWhatsapp && Boolean(interesaWhatsappHref);

          return (
            <div
              key={itemId}
              className={`${styles.availability__item} ${isExpanded ? styles['availability__item--expanded'] : ''}`}
            >
              {/* Main row */}
              <div
                className={styles.availability__row}
                onClick={() => toggleItem(itemId)}
              >
                <div className={styles.availability__cell}>
                  <span className={styles.availability__itemName}>
                    {item.titulo || '—'}
                  </span>
                </div>
                {main.map((f, i) => (
                  <div key={i} className={styles.availability__cell}>
                    {formatFeatureValue(f)}
                  </div>
                ))}
                {/* Pad empty cells if not enough main features */}
                {Array.from({ length: Math.max(0, 3 - main.length) }).map((_, i) => (
                  <div key={`pad-${i}`} className={styles.availability__cell}>—</div>
                ))}
                <div className={styles.availability__cellActions}>
                  {hasCotizador && (
                    <button
                      className={styles.availability__btnCotizar}
                      title={quoteLabel}
                      onClick={(event) => handleCotizadorClick(event, item)}
                      type="button"
                    >
                      <img src={iconCotizar} alt={quoteLabel} title={quoteLabel} className={styles.availability__btnIcon} aria-hidden="true" />
                      {quoteLabel}
                    </button>
                  )}
                  {showInterestButton && (
                    <a
                      className={[
                        styles.availability__btnInteresa,
                        !interestLabel ? styles['availability__btnInteresa--iconOnly'] : '',
                      ].filter(Boolean).join(' ')}
                      title={interestLabel || undefined}
                      aria-label={interestLabel || undefined}
                      href={interesaHref}
                      onClick={(e) => e.stopPropagation()}
                      target={interesaIsWhatsapp ? '_blank' : undefined}
                      rel={interesaIsWhatsapp ? 'noopener noreferrer' : undefined}
                    >
                      <img src={iconCorreo} alt="" className={styles.availability__btnIcon} aria-hidden="true" />
                      {interestLabel}
                    </a>
                  )}
                </div>
                <div className={styles.availability__chevron}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    className={`${styles.availability__chevronIcon} ${isExpanded ? styles['availability__chevronIcon--open'] : ''}`}
                  >
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>

              {/* Expandable detail */}
              <div
                className={`${styles.availability__detail} ${isExpanded ? styles['availability__detail--open'] : ''}`}
              >
                <div className={styles.availability__detailInner}>
                  {secondary.map((f, i) => (
                    <div key={i} className={styles.availability__detailItem}>
                      <span className={styles.availability__detailLabel}>{f.atributo}</span>
                      <span className={styles.availability__detailValue}>{formatFeatureValue(f)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: disclaimer + pagination */}
      <div className={styles.availability__footer}>
        <p className={styles.availability__disclaimer}>
          {disclaimer}
        </p>
        {!showAll && totalPages > 1 && (
        <div className={styles.availability__pagination}>
          <span className={styles.availability__pageInfo}>
            {currentPage + 1}–{totalPages}
          </span>
          <button
            className={styles.availability__pageBtn}
            onClick={() => handlePageChange(currentPage - 1, 'right')}
            disabled={currentPage === 0}
            aria-label={isEn ? 'Previous' : 'Anterior'}
          >
            ‹
          </button>
          <button
            className={styles.availability__pageBtn}
            onClick={() => handlePageChange(currentPage + 1, 'left')}
            disabled={currentPage >= totalPages - 1}
            aria-label={isEn ? 'Next' : 'Siguiente'}
          >
            ›
          </button>
        </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default AvailabilityList;
